import { GroupState, SideBarList } from '@/composition/groups'
import {
  AnalyticsService,
  DocumentsAndPayments,
  NotificationMobileView,
  ProfileContainer,
} from '@ghl/clientportal-core'
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'

import ErrorComponent from '@/components/common/Error.vue'
import PostView from '@/components/posts/PostView.vue'
import { app } from '@/composition/app'
import { getSessionCookieData, isGokollab } from '@ghl/clientportal-core/init'
import { defineAsyncComponent } from 'vue'
import GroupContainer from '../pages/GroupContainer.vue'
import Groups from '../pages/GroupsPage.vue'
import { requireAuth } from './guards'
import { RouterHelper } from './router.helper'

const ChatViewIndex = defineAsyncComponent({
  loader: () => import('@/components/chats/ChatViewIndex.vue'),
  errorComponent: ErrorComponent,
})

const ConversationsMobile = defineAsyncComponent({
  loader: () => import('@/components/chats/mobile/ConversationsMobile.vue'),
  errorComponent: ErrorComponent,
})

// import from clientportal-core
import { MorePage } from '@ghl/clientportal-core'
// const MorePage = defineAsyncComponent({
//   loader: () => import('@ghl/clientportal-core'),
//   errorComponent: ErrorComponent,
// })

const GroupSettings = defineAsyncComponent({
  loader: () => import('@/components/groups/settings/GroupSettings.vue'),
  errorComponent: ErrorComponent,
})

const Learning = defineAsyncComponent({
  loader: () => import('@/pages/LearningPage.vue'),
  errorComponent: ErrorComponent,
})

const GroupMembers = defineAsyncComponent({
  loader: () => import('../components/groups/groupMembers/GroupMembers.vue'),
  errorComponent: ErrorComponent,
})

const CommunitySettings = defineAsyncComponent({
  loader: () => import('../components/reports/CommunitySettings.vue'),
  errorComponent: ErrorComponent,
})
const ContentReports = defineAsyncComponent({
  loader: () => import('../components/reports/ContentReports.vue'),
  errorComponent: ErrorComponent,
})
const CommunitiesHome = defineAsyncComponent({
  loader: () => import('../pages/CommunitiesHome.vue'),
  errorComponent: ErrorComponent,
})

const routes: Readonly<RouteRecordRaw[]> = [
  {
    path:
      import.meta.env.VITE_JS_IS_GOKOLLAB == 'true'
        ? '/:groupSlug'
        : '/communities/groups/:groupSlug',
    component: GroupContainer,
    meta: {
      priority: 'high',
    },
    name: 'communities',
    children: [
      {
        name: 'home',
        path: 'home',
        meta: {
          priority: 'high',
        },
        component: Groups,
        children: [
          {
            name: 'group-post-view',
            path: 'posts/:postId',
            component: PostView,
            props: true,
          },
        ],
      },
      {
        path: 'search',
        name: 'group-home-search',
        component: () => import('../pages/SearchView.vue'),
        props: true,
        children: [
          {
            name: 'group-search-post-view',
            path: 'posts/:postId',
            component: PostView,
            props: true,
          },
        ],
      },

      {
        path: 'content-unavailable',
        name: 'content-unavailable',
        component: () => import('@/pages/ContentUnavailable.vue'),
      },
      {
        path: 'clientportal-home',
        name: 'clientportal-home',
        component: CommunitiesHome,
        beforeEnter: requireAuth,
      },
      {
        path: 'profile',
        name: 'profile-container',
        component: ProfileContainer,
        beforeEnter: requireAuth,
      },
      {
        path: 'documents-and-payments',
        name: 'documents-and-payments',
        component: DocumentsAndPayments,
        beforeEnter: (_to, from, next) => {
          if (app.state.isMobile && isGokollab()) {
            next()
          } else {
            next(from)
          }
        },
      },
      {
        name: 'no-communities-mobile',
        path: 'no-communities',
        component: () => import('@/pages/NoCommunities.vue'),
      },

      {
        name: 'channel-overview',
        path: 'channels/:channelSlug',
        component: () => import('../pages/ChannelsPage.vue'),
        children: [
          {
            name: 'channel-post-view',
            path: 'posts/:postId',
            component: PostView,
            props: true,
          },
        ],
      },

      {
        path: 'channels/:channelSlug/search',
        name: 'channel-search',
        component: () => import('../pages/SearchView.vue'),
        props: true,
        children: [
          {
            name: 'channel-search-post-view',
            path: 'posts/:postId',
            component: PostView,
            props: true,
          },
        ],
      },

      {
        name: 'group-member-list',
        path: 'member-list',
        component: GroupMembers,
      },
      {
        // This routes to contents reports page for mobile and group settings with Reported Content tab selected for web
        name: 'to-review',
        path: 'to-review',
        component: app.state.isMobile ? ContentReports : GroupSettings,
        props: { show: true },
      },
      {
        // this route is only for mobile
        name: 'communities-settings',
        path: 'communities-settings',
        component: CommunitySettings,
        beforeEnter: (_to, from, next) => {
          if (app.state.isMobile) {
            next()
          } else {
            next(from)
          }
        },
      },
      {
        // this route is only for mobile
        name: 'communities-more',
        path: 'communities-more',
        component: MorePage,
        beforeEnter: (_to, from, next) => {
          if (app.state.isMobile) {
            next()
          } else {
            next(from)
          }
        },
      },

      {
        // this route is only for mobile
        name: 'conversations',
        path: 'conversations',
        component: ConversationsMobile,

        beforeEnter: (_to, from, next) => {
          if (app.state.isMobile) {
            next()
          } else {
            next(from)
          }
        },
      },
      {
        // this route is only for mobile
        name: 'chat-view',
        path: 'conversations/:conversationId',
        component: ChatViewIndex,
        props: true,
        beforeEnter: (_to, from, next) => {
          if (app.state.isMobile) {
            next()
          } else {
            next(from)
          }
        },
      },
      {
        name: 'user-profile',
        path: 'users/:userSlug',
        component: () => import('@/pages/UserProfile.vue'),
        children: [
          {
            name: 'user-post-view',
            path: 'posts/:postId',
            component: () => import('../components/posts/PostView.vue'),
            props: true,
          },
        ],
      },
      {
        name: 'learning-home',
        path: 'learning',
        component: Learning,
      },
      {
        // need to keep name in this format,it's required for red dot in common bottombar
        name: 'NotificationMobileView',
        path: 'notification-mobile',
        component: NotificationMobileView,
        beforeEnter: (_to, from, next) => {
          if (app.state.isMobile) {
            next()
          } else {
            next(from)
          }
        },
      },
      {
        name: 'events',
        path: 'events',
        component: () => import('@/pages/CalendarPage.vue'),
      },
      {
        name: 'leaderboard',
        path: 'leaderboard',
        component: () => import('@/pages/LeaderboardPage.vue'),
      },
      {
        name: 'about',
        path: 'about',
        component: () => import('@/pages/AboutPage.vue'),
      },
      {
        // this route is only for mobile
        name: 'my-courses',
        path: 'my-courses',
        component: () => import('@/pages/MyCourses.vue'),
        beforeEnter: (_to, from, next) => {
          if (app.state.isMobile) {
            next()
          } else {
            next(from)
          }
        },
      },

      {
        name: 'inactive-group',
        path: 'inactive-group',
        component: () => import('@/pages/InactiveGroup.vue'),
      },
      {
        name: 'private-group',
        path: 'private-group',
        component: () => import('@/pages/PrivateGroup.vue'),
      },
    ],
  },

  {
    path: '/404',
    name: 'not-found',
    component: () => import('@/pages/NotFound.vue'),
  },
  {
    name: 'no-communities',
    path: '/communities/no-communities',
    component: () => import('@/pages/NoCommunities.vue'),
  },
  {
    path: '/:catchAll(.*)',
    name: 'communities-base',
    component: () => import('../pages/GroupContainer.vue'),
  },
]

const postViewPages = {
  'group-post-view': true,
  'user-post-view': true,
  'channel-post-view': true,
  'group-home-search': true,
  'channel-search': true,
}
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from) {
    if (
      (to?.name && to.name in postViewPages) ||
      (from?.name && from.name in postViewPages)
    ) {
      return
    }
    // always scroll to top
    return { top: 0 }
  },
})

router.beforeEach(async (to, from) => {
  if (to.name === 'private-group' || to.name === 'inactive-group') {
    GroupState.state.sideBarListView = SideBarList.GROUPS
    GroupState.state.closedGroup = true
  } else {
    GroupState.state.sideBarListView = SideBarList.CHANNELS
    GroupState.state.closedGroup = false
  }
  await Promise.all([
    RouterHelper.handleBannedUser(to, router),
    RouterHelper.handlePrivateGroups(to, router, from),
    RouterHelper.handleInactiveGroups(to, router),
  ])

  //Send tracking event for each page hit
  const sessionCookieData = getSessionCookieData()
  if (sessionCookieData && sessionCookieData?.locationId) {
    AnalyticsService.trackPageViewEvents({
      locationId: sessionCookieData?.locationId,
      eventType: 'communites_user_page_hit',
      route: to.path,
    }).catch(error => {
      console.error('Failed to send tracking event:', error)
    })
    AnalyticsService.trackPageViewEvents({
      locationId: sessionCookieData?.locationId,
      eventType: 'clientportal_user_page_hit',
      route: to.path,
    }).catch(error => {
      console.error('Failed to send tracking event:', error)
    })
  }
})

// router.js
router.afterEach(() => {
  RouterHelper.checkAndShowMandatoryInfoModel()
})
export { router }
